import React from 'react';
import { Modal } from 'react-bootstrap';
import { ArrowLeftIcon, XCloseIcon } from './transactional-icons';
import './transactional-sheet.scss';

/**
 * How a transactional view (credential, payment card, confirm spend) is
 * presented.
 *
 * - `modal`: layered over the app on a blurred Ink scrim. Escape / scrim
 *   dismiss only when `onClose` is given.
 * - `standalone`: the view *is* the page (WhatsApp-linked `/artifact/:token`
 *   pages). Canvas background, bordered card on desktop, full-bleed on
 *   phones. Never a scrim.
 */
export type SheetPresentation = 'modal' | 'standalone';

export interface TransactionalSheetProps {
  show: boolean;
  presentation?: SheetPresentation;
  /**
   * Renders the close control and drives Escape / scrim dismissal. Leave
   * undefined when there is nothing truthful for a close to do (standalone
   * pages, desktop no-close variants).
   */
  onClose?: () => void;
  /** Renders the back control — stacked flows (add card from confirm spend). */
  onBack?: () => void;
  /**
   * When true, Escape / scrim are swallowed — used while an in-sheet
   * confirmation is open or a submit is in flight.
   */
  holdOpen?: boolean;
  /** Disables the header controls (e.g. while submitting). */
  controlsDisabled?: boolean;
  /**
   * 32px accent glyph shown top-left when there is no back control
   * (lock for credentials, bag for spend).
   */
  leading?: React.ReactNode;
  ariaLabelledBy?: string;
  className?: string;
  backdropClassName?: string;
  /** Scrollable body. */
  children: React.ReactNode;
  /** Pinned footer; omitted on the success screen. */
  footer?: React.ReactNode;
  /** Ref to the footer element (e.g. for a tray that grows out of it). */
  footerRef?: React.Ref<HTMLDivElement>;
  footerClassName?: string;
  /** Rendered inside the card after the footer (e.g. the add-funds tray). */
  afterFooter?: React.ReactNode;
  /** Rendered over the card — the in-sheet confirm. */
  overlay?: React.ReactNode;
}

const noop = () => undefined;

const TransactionalSheet: React.FC<TransactionalSheetProps> = ({
  show,
  presentation = 'modal',
  onClose,
  onBack,
  holdOpen,
  controlsDisabled,
  leading,
  ariaLabelledBy,
  className,
  backdropClassName,
  children,
  footer,
  footerRef,
  footerClassName,
  afterFooter,
  overlay,
}) => {
  const standalone = presentation === 'standalone';
  const rootClassName = [
    'tx-sheet',
    standalone ? 'tx-sheet--standalone' : 'tx-sheet--modal',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const hasControls = Boolean(onBack || onClose || leading);
  const controls = hasControls ? (
    <div className='tx-controls'>
      {onBack ? (
        <button
          className='tx-control tx-control--back'
          type='button'
          aria-label='Back'
          onClick={onBack}
          disabled={controlsDisabled}
        >
          <ArrowLeftIcon />
        </button>
      ) : leading ? (
        <span className='tx-leading' aria-hidden='true'>
          {leading}
        </span>
      ) : null}
      {onClose && (
        <button
          className='tx-control tx-control--close'
          type='button'
          aria-label='Close'
          onClick={onClose}
          disabled={controlsDisabled}
        >
          <XCloseIcon />
        </button>
      )}
    </div>
  ) : null;

  if (standalone) {
    if (!show) return null;
    return (
      <div
        className={rootClassName}
        role='dialog'
        aria-labelledby={ariaLabelledBy}
      >
        <div className='tx-card'>
          <div className='tx-body'>
            {controls}
            {children}
          </div>
          {footer && (
            <div
              className={['tx-footer', footerClassName]
                .filter(Boolean)
                .join(' ')}
              ref={footerRef}
            >
              {footer}
            </div>
          )}
          {afterFooter}
          {overlay}
        </div>
      </div>
    );
  }

  return (
    <Modal
      show={show}
      onHide={holdOpen || !onClose ? noop : onClose}
      centered
      scrollable
      dialogClassName={rootClassName}
      contentClassName='tx-card'
      backdropClassName={['tx-sheet-backdrop', backdropClassName]
        .filter(Boolean)
        .join(' ')}
      aria-labelledby={ariaLabelledBy}
    >
      <Modal.Body className='tx-body'>
        {controls}
        {children}
      </Modal.Body>
      {footer && (
        <Modal.Footer
          className={['tx-footer', footerClassName].filter(Boolean).join(' ')}
          ref={footerRef}
        >
          {footer}
        </Modal.Footer>
      )}
      {afterFooter}
      {overlay}
    </Modal>
  );
};

export default TransactionalSheet;
